/**
 * Every desktop control, for the controls panel (2026-10-06, the user: the HUD's single line
 * showed only a few). Grouped by what people are doing; `where` marks controls that only
 * work in the lobby or in a study room. Keep in step with the key handlers in App.ts,
 * Locomotion.ts, PanelManipulator.ts, ModelShelf.ts, Ward.ts, RoomScene.ts and LobbyScene.ts.
 */
export interface Control {
  /** Keys or mouse actions, each shown as a key cap. */
  keys: string[];
  what: string;
}

export interface ControlGroup {
  title: string;
  where?: 'lobby' | 'room';
  controls: Control[];
}

export const CONTROLS: ControlGroup[] = [
  {
    title: 'Getting around',
    controls: [
      { keys: ['W', 'A', 'S', 'D'], what: 'Walk (the arrow keys work too)' },
      { keys: ['Shift'], what: 'Hold to walk faster' },
      { keys: ['Mouse'], what: 'Look around; click the view first to capture the mouse' },
      { keys: ['Q', 'E'], what: 'Turn left or right in steps (30° or 45°, set in the menu)' },
      { keys: ['R'], what: 'Reset your eye height' },
      { keys: ['Esc'], what: 'Free the mouse cursor; click the view to capture it again' },
      {
        keys: ['Tab'],
        what: 'Open or close the wrist menu: go to, comfort, collaborate, react, leave',
      },
      { keys: ['M'], what: 'Mute or unmute your microphone' },
      { keys: ['?'], what: 'Show or hide this list' },
    ],
  },
  {
    title: 'Pointing and moving things',
    controls: [
      {
        keys: ['Click'],
        what: 'Select what the centre dot points at (or the cursor, when free): buttons, chart elements, records, tokens',
      },
      { keys: ['Hold G'], what: 'Grab and carry the screen or 3D model you point at' },
      { keys: ['Wheel'], what: 'Scale the screen or model you point at' },
      { keys: ['H'], what: 'Send the screen you point at back to its place' },
      { keys: [',', '.'], what: 'Turn a 3D model you are holding' },
      { keys: ['Keyboard'], what: 'Type into any open text field; Enter confirms, Esc closes it' },
    ],
  },
  {
    title: 'Lobby',
    where: 'lobby',
    controls: [
      {
        keys: ['1', '…', '9'],
        what: 'Enter that study on the directory page (asks for the room code)',
      },
      { keys: ['Enter'], what: 'Confirm the name, room code or other typed entry' },
    ],
  },
  {
    title: 'In a study room',
    where: 'room',
    controls: [
      { keys: ['C'], what: 'Talk to Luma, the room host' },
      { keys: ['L'], what: 'Leave for the lobby (or walk out through the door)' },
      { keys: ['T'], what: 'Tidy: every screen back home, annotate and point off' },
      { keys: ['O'], what: 'Pull a copy of the wall display in front of you' },
      { keys: ['N'], what: 'Annotate: select chart elements to pin a note on them' },
      { keys: ['P'], what: 'Point: a beam from you that everyone sees' },
      { keys: ['F'], what: 'Follow the next person in the room (again to stop)' },
      { keys: ['Hold G'], what: 'In the ward: pull a patient out for review, and drag them' },
      { keys: ['G', 'G'], what: 'Twice quickly on a pulled-out patient: send them back' },
      { keys: ['Z'], what: 'Save a screenshot of your view (on the query log page)' },
      { keys: ['U'], what: 'Share your screen on the presentation wall, or stop' },
    ],
  },
  {
    title: 'Presenting',
    where: 'room',
    controls: [
      { keys: ['I'], what: 'Take the lectern and present, or stop' },
      { keys: [']', '['], what: 'Next or previous slide' },
      { keys: ['K'], what: 'Spotlight on or off' },
      { keys: ['J'], what: "Accept the presenter's invitation to join them" },
    ],
  },
  {
    title: 'Reactions',
    where: 'room',
    controls: [
      { keys: ['Y'], what: 'Raise or lower your hand' },
      { keys: ['B'], what: 'Thumbs up' },
      { keys: ['V'], what: 'Clap' },
      { keys: ['X'], what: 'Wave' },
      { keys: ['/'], what: 'Question mark' },
    ],
  },
];
